import {
  ARTICLE_SECTION_RULES,
  DEFAULT_SECTION_RULES,
  HIDDEN_FIELD_FALLBACK_DEFAULTS,
} from "./articleSectionRules";

export const DEFAULT_DISPLAY_ORDER = 1000;

const IMAGE_FIELD_MAP = {
  contentImage: ["contentImageFile", "contentImagePreview"],
  bannerImage: ["bannerImageFile", "bannerImagePreview"],
};

const FIELD_CLEAR_VALUES = {
  subSectionId: "",
  articleTag: "",
  detailPageUrl: "",
  authorName: "",
  sourceName: "",
  readingTime: "",
  publishedOn: "",
  eventsFrom: "",
  eventsTo: "",
  eventLocation: "",
};

/** Normalize section label for rule lookup. */
export const normalizeSectionName = (name) => {
  if (name === null || name === undefined) return "";
  return String(name)
    .trim()
    .toLowerCase()
    .replace(/\u2019/g, "'")
    .replace(/\s+/g, " ");
};

export const getSectionRules = (sectionName) => {
  const key = normalizeSectionName(sectionName);
  if (!key) return { ...DEFAULT_SECTION_RULES };
  const rule = ARTICLE_SECTION_RULES[key];
  if (!rule) return { ...DEFAULT_SECTION_RULES };
  return {
    hiddenFields: rule.hiddenFields || [],
    requiredFields: rule.requiredFields || [],
    defaultValues: rule.defaultValues || {},
  };
};

const coercePayloadScalar = (value) => {
  if (value === undefined || value === null) {
    return "";
  }

  const normalized = String(value).trim();
  if (normalized.toUpperCase() === "NA") {
    return "";
  }

  return normalized;
};

export const getHiddenFieldFallbackValue = (fieldName, sectionDefaultValues = {}) => {
  if (
    Object.prototype.hasOwnProperty.call(sectionDefaultValues, fieldName)
  ) {
    return coercePayloadScalar(sectionDefaultValues[fieldName]);
  }
  if (
    Object.prototype.hasOwnProperty.call(
      HIDDEN_FIELD_FALLBACK_DEFAULTS,
      fieldName
    )
  ) {
    return coercePayloadScalar(HIDDEN_FIELD_FALLBACK_DEFAULTS[fieldName]);
  }
  return coercePayloadScalar("");
};

const applyHiddenFieldToPayloadShape = (target, fieldName, fallbackValue) => {
  if (IMAGE_FIELD_MAP[fieldName]) {
    IMAGE_FIELD_MAP[fieldName].forEach((key) => {
      if (key.includes("File")) {
        target[key] = null;
      } else {
        target[key] = "";
      }
    });
    return;
  }
  target[fieldName] = fallbackValue;
};

/**
 * Applies section rules: visible fields keep user values; hidden fields get safe fallbacks.
 * @param {object} formData
 * @param {string} sectionName - display name from section dropdown
 * @returns {object} payload-ready plain object (no undefined/null scalars)
 */
export const buildArticlePayloadBySectionRules = (formData, sectionName) => {
  const rules = getSectionRules(sectionName);
  const sanitized = {
    ...formData,
    sectionId: String(formData.sectionId ?? ""),
    title: String(formData.title ?? "").trim(),
    contentSlug: String(formData.contentSlug ?? "").trim(),
    shortDescription: String(formData.shortDescription ?? "").trim(),
    fullDescription: formData.fullDescription ?? "",
    metaTitle: String(formData.metaTitle ?? "").trim(),
    metaDescription: String(formData.metaDescription ?? "").trim(),
    metaKeywords: String(formData.metaKeywords ?? "").trim(),
    detailPageUrl: String(formData.detailPageUrl ?? "").trim(),
    authorName: String(formData.authorName ?? "").trim(),
    sourceName: String(formData.sourceName ?? "").trim(),
    readingTime: String(formData.readingTime ?? "").trim(),
    publishedOn: String(formData.publishedOn ?? "").trim(),
    eventsFrom: String(formData.eventsFrom ?? "").trim(),
    eventsTo: String(formData.eventsTo ?? "").trim(),
    eventLocation: String(formData.eventLocation ?? "").trim(),
    subSectionId: String(formData.subSectionId ?? "").trim(),
    articleTag: String(formData.articleTag ?? "").trim(),
    isFeatured: Boolean(formData.isFeatured),
    isFeaturedHome: Boolean(formData.isFeaturedHome),
    isPremium: Boolean(formData.isPremium),
    displayOrder: resolveDisplayOrder(formData.displayOrder),
    contentImageFile: formData.contentImageFile ?? null,
    contentImagePreview: formData.contentImagePreview ?? "",
    bannerImageFile: formData.bannerImageFile ?? null,
    bannerImagePreview: formData.bannerImagePreview ?? "",
  };

  (rules.hiddenFields || []).forEach((fieldName) => {
    const fallback = getHiddenFieldFallbackValue(
      fieldName,
      rules.defaultValues
    );
    applyHiddenFieldToPayloadShape(sanitized, fieldName, fallback);
  });

  return sanitized;
};

export const getSectionNameFromOptions = (sectionId, sectionOptions = []) => {
  if (!sectionId) return "";
  const match = sectionOptions.find(
    (opt) => String(opt.value) === String(sectionId)
  );
  return match?.label ?? "";
};

export const isFieldHidden = (fieldName, rules) =>
  Boolean(rules?.hiddenFields?.includes(fieldName));

export const isFieldRequired = (fieldName, rules) =>
  Boolean(rules?.requiredFields?.includes(fieldName));

/** Values to reset when a field becomes hidden. */
export const getClearedValuesForHiddenFields = (hiddenFields = []) => {
  const updates = {};
  hiddenFields.forEach((field) => {
    if (IMAGE_FIELD_MAP[field]) {
      IMAGE_FIELD_MAP[field].forEach((key) => {
        updates[key] = key.includes("File") ? null : "";
      });
    } else if (Object.prototype.hasOwnProperty.call(FIELD_CLEAR_VALUES, field)) {
      updates[field] = FIELD_CLEAR_VALUES[field];
    }
  });
  return updates;
};

export const resolveDisplayOrder = (value) => {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return DEFAULT_DISPLAY_ORDER;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_DISPLAY_ORDER;
};

const mapFieldToErrorKey = (field) => {
  if (field === "contentImage" || field === "bannerImage") return field;
  return field;
};

/** Clear validation errors for hidden fields. */
export const clearErrorsForHiddenFields = (errors, hiddenFields = []) => {
  const next = { ...errors };
  hiddenFields.forEach((field) => {
    const key = mapFieldToErrorKey(field);
    if (key && Object.prototype.hasOwnProperty.call(next, key)) {
      next[key] = "";
    }
  });
  return next;
};

export const hasArticleImage = (formData, imageField) => {
  if (imageField === "contentImage") {
    return Boolean(
      String(formData.contentImageUrl || "").trim() ||
        String(formData.contentImagePreview || "").trim() ||
        formData.contentImageFile
    );
  }
  if (imageField === "bannerImage") {
    return Boolean(
      String(formData.bannerImageUrl || "").trim() ||
        String(formData.bannerImagePreview || "").trim() ||
        formData.bannerImageFile
    );
  }
  return false;
};

export const isEventSettingsVisible = (rules) =>
  !isFieldHidden("eventsFrom", rules) ||
  !isFieldHidden("eventsTo", rules) ||
  !isFieldHidden("eventLocation", rules);
